"use client";

import { useEffect, useRef, useState } from "react";
import { L, SHADOWS } from "./tokens";
import { ScrollReveal } from "./ScrollReveal";
import { PillButton } from "./PillButton";
import { PriceCountUp } from "./PriceCountUp";
import type { PublicPlanConfig } from "@/app/actions/public-plan-configs";

const PLAN_COPY: Record<string, { name: string; desc: string; feats: string[] }> = {
  lite: {
    name: "Lite",
    desc: "Para freelancers o equipos chicos con una búsqueda activa.",
    feats: [
      "Portal de empleo con tu marca",
      "Análisis de CVs con IA",
      "Pipeline kanban",
      "Plantillas de email",
      "Soporte por email",
    ],
  },
  starter: {
    name: "Starter",
    desc: "Para equipos que recién arrancan a sistematizar reclutamiento.",
    feats: [
      "Todo lo de Lite",
      "Más búsquedas y usuarios en paralelo",
      "Análisis IA avanzado",
      "Plantillas de email ilimitadas",
      "Soporte prioritario",
    ],
  },
  growth: {
    name: "Growth",
    desc: "Para equipos con múltiples búsquedas abiertas en paralelo.",
    feats: [
      "Todo lo de Starter",
      "Pool de talentos",
      "Scorecards con peso por criterio",
      "Analytics: funnel, time-to-hire, fuente",
      "Emails masivos + recordatorios automáticos",
      "Gestión de equipo y permisos",
      "Soporte prioritario",
    ],
  },
};

const FALLBACK: Record<string, { price: number; jobs: number; users: number }> = {
  lite: { price: 0, jobs: 1, users: 1 },
  starter: { price: 79, jobs: 3, users: 5 },
  growth: { price: 149, jobs: 10, users: 15 },
};

const PLAN_KEYS = ["lite", "starter", "growth"] as const;

export function Pricing({ plans, trialDays, locale }: { plans: PublicPlanConfig[]; trialDays: number; locale: string }) {
  const byPlan = (plan: string) => plans.find((p) => p.plan === plan);
  const ctaLabel = trialDays > 0 ? `Empezar ${trialDays} día${trialDays === 1 ? "" : "s"} gratis` : "Suscribirme";

  // priceSlider: recomienda un plan según cuántas búsquedas abiertas tenés.
  const [searches, setSearches] = useState(3);
  const isEnterprise = searches >= 12;
  const recoIdx = isEnterprise ? -1 : searches <= 1 ? 0 : searches <= 3 ? 1 : 2;

  // priceCount / pchecks: cuentan/staggean al entrar en viewport.
  const [played, setPlayed] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setPlayed(true); io.unobserve(e.target); } }); },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // priceDim: al hacer hover sobre una card, las demás se atenúan.
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="planes" style={{ background: L.bgWarm, padding: "110px 0" }}>
      <div className="mx-auto max-w-[1240px] px-8">
        <ScrollReveal>
          <div className="mx-auto max-w-[620px] text-center">
            <h2 style={{ fontFamily: L.fontDisplay, fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 0.98, fontSize: "clamp(40px, 5.2vw, 68px)", color: L.ink, margin: "0 0 18px" }}>
              Precio simple,<br /><em style={{ fontStyle: "normal", color: L.inkFaint }}>sin sorpresas.</em>
            </h2>
            <p className="mx-auto" style={{ fontSize: 19, lineHeight: 1.45, color: L.inkSoft, maxWidth: 620 }}>
              Pago mensual en USD. Sin permanencia, sin costo por candidato. Cancelás cuando quieras.
            </p>
          </div>
        </ScrollReveal>

        {/* priceSlider */}
        <div className="mx-auto mt-10 max-w-[560px] text-center">
          <p style={{ fontSize: 14.5, fontWeight: 600, color: L.ink, marginBottom: 14 }}>
            ¿Cuántas búsquedas abiertas tenés? <b style={{ color: L.violet }}>{isEnterprise ? "12+" : searches}</b>
          </p>
          <input
            type="range"
            min={1}
            max={12}
            value={searches}
            onChange={(e) => setSearches(Number(e.target.value))}
            className="mx-auto block w-full max-w-[420px] cursor-pointer"
            style={{ accentColor: L.violet }}
            aria-label="Cantidad de búsquedas abiertas"
          />
          <div className="mx-auto mt-1 flex max-w-[420px] justify-between" style={{ fontFamily: L.fontMono, fontSize: 11, color: L.inkFaint }}>
            <span>1</span><span>3</span><span>6</span><span>9</span><span>12+</span>
          </div>
          <p
            style={{
              marginTop: 10, fontSize: 13, color: L.inkSoft,
              opacity: isEnterprise ? 1 : 0, height: isEnterprise ? "auto" : 0, overflow: "hidden",
              transition: "opacity .25s ease",
            }}
          >
            ¿Más de 12 búsquedas? Sos enterprise.{" "}
            <a href="mailto:hola@talenv.com" style={{ color: L.violet, fontWeight: 500 }}>Escribinos →</a>
          </p>
        </div>

        <div ref={gridRef} className="group/grid mx-auto mt-8 grid max-w-[1000px] grid-cols-1 gap-5 md:grid-cols-3">
          {PLAN_KEYS.map((planKey, i) => {
            const copy = PLAN_COPY[planKey];
            const cfg = byPlan(planKey);
            const fallback = FALLBACK[planKey];
            const price = cfg?.price_monthly_usd ?? fallback.price;
            const jobs = cfg?.max_jobs ?? fallback.jobs;
            const users = cfg?.max_users ?? fallback.users;
            const popular = planKey === "growth";
            const isReco = i === recoIdx;
            const isDimmed = hovered !== null && hovered !== i;
            return (
              <ScrollReveal key={planKey} delay={i * 90}>
                <div
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  className="relative flex h-full flex-col rounded-2xl p-6"
                  style={{
                    background: popular ? L.ink : "#fff",
                    color: popular ? "#fff" : L.ink,
                    border: popular ? "none" : `1px solid ${isReco ? L.violet : L.line}`,
                    // priceSpot: la card popular se agranda un toque; pm-reco: borde+shadow violeta cuando el slider la recomienda.
                    boxShadow: isReco
                      ? `0 0 0 2px ${L.violet}, 0 20px 44px -18px rgba(107,92,255,.4)`
                      : popular ? SHADOWS.popular : "none",
                    transform: popular ? "scale(1.03)" : "none",
                    zIndex: popular ? 2 : 1,
                    opacity: isDimmed ? 0.55 : 1,
                    filter: isDimmed ? "saturate(.85)" : "none",
                    transition: "opacity .3s, transform .3s, filter .3s, box-shadow .3s",
                  }}
                >
                  {/* pm-reco-tag: solo visible si esta card es la recomendada por el slider */}
                  {isReco && (
                    <span
                      className="absolute -top-3 left-6 rounded-full px-2.5 py-1"
                      style={{ background: L.violet, color: "#fff", fontFamily: L.fontMono, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}
                    >
                      Recomendado
                    </span>
                  )}
                  {popular && !isReco && (
                    <span
                      className="tl-badge-pulse absolute -top-3 right-4 overflow-hidden rounded-full px-2.5 py-1"
                      style={{ background: L.lime, color: L.bgDeep, fontFamily: L.fontMono, fontSize: 10.5, fontWeight: 700 }}
                    >
                      Más elegido
                    </span>
                  )}
                  <h3 style={{ fontFamily: L.fontDisplay, fontSize: 20, fontWeight: 700, margin: 0 }}>{copy.name}</h3>
                  <p style={{ fontSize: 13.5, color: popular ? "rgba(255,255,255,.65)" : L.inkSoft, margin: "6px 0 18px", minHeight: 40 }}>{copy.desc}</p>
                  <div className="flex items-baseline gap-1">
                    <span style={{ fontSize: 13, fontWeight: 600, color: popular ? "rgba(255,255,255,.6)" : L.inkSoft }}>USD</span>
                    <span style={{ fontFamily: L.fontDisplay, fontSize: 42, fontWeight: 700 }}>
                      <PriceCountUp target={price} play={played} delay={i * 220} />
                    </span>
                    <span style={{ fontSize: 13, color: popular ? "rgba(255,255,255,.6)" : L.inkSoft }}>/ mes</span>
                  </div>
                  <div className="mt-2" style={{ fontSize: 12.5, color: popular ? "rgba(255,255,255,.7)" : L.inkSoft }}>
                    Hasta <b style={{ color: popular ? "#fff" : L.ink }}>{jobs} búsqueda{jobs === 1 ? "" : "s"}</b> · <b style={{ color: popular ? "#fff" : L.ink }}>{users} usuario{users === 1 ? "" : "s"}</b>
                  </div>

                  {/* priceSocial: solo en la card popular */}
                  {popular && (
                    <div className="mb-1 mt-2.5 flex items-center gap-1.5" style={{ fontSize: 12, color: "rgba(255,255,255,.75)" }}>
                      <span className="inline-flex">
                        <i className="flex h-[18px] w-[18px] items-center justify-center rounded-full" style={{ border: `1.5px solid ${L.ink}`, background: "#8F84FF", color: L.bgDeep, fontSize: 8, fontWeight: 700, fontStyle: "normal" }}>MJ</i>
                        <i className="-ml-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full" style={{ border: `1.5px solid ${L.ink}`, background: L.lime, color: L.bgDeep, fontSize: 8, fontWeight: 700, fontStyle: "normal" }}>LC</i>
                        <i className="-ml-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full" style={{ border: `1.5px solid ${L.ink}`, background: L.amber, color: L.bgDeep, fontSize: 8, fontWeight: 700, fontStyle: "normal" }}>AP</i>
                      </span>
                      El 68% de los equipos elige Growth
                    </div>
                  )}

                  <ul className={`${popular ? "mt-3" : "mt-5"} mb-6 flex flex-1 flex-col gap-2`}>
                    {copy.feats.map((f, fi) => (
                      <li
                        key={f}
                        className="tl-feat-in flex items-start gap-2"
                        style={{
                          fontSize: 13.5, color: popular ? "rgba(255,255,255,.85)" : L.ink2,
                          opacity: played ? undefined : 0,
                          transform: played ? undefined : "translateX(-8px)",
                          transitionDelay: `${fi * 0.1}s`,
                        }}
                      >
                        <span style={{ color: popular ? L.lime : L.violet, flexShrink: 0 }}>✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <PillButton href={`/${locale}/register?plan=${planKey}`} variant={popular ? "violet" : "outline"} fullWidth>
                      {ctaLabel}
                    </PillButton>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <p className="mt-8 text-center" style={{ fontSize: 14, color: L.inkSoft }}>
          {trialDays > 0
            ? `Tras los ${trialDays} días, se cobra el plan que elijas. Cancelás antes y no se te cobra nada.`
            : "Se cobra al instante el plan que elijas. Cancelás cuando quieras y no se cobra el próximo mes."}
          {" "}¿Necesitás algo a medida o sos enterprise?{" "}
          <a href="mailto:hola@talenv.com" style={{ color: L.violet, fontWeight: 500 }}>Escribinos →</a>
        </p>

        {/* priceTrust */}
        <div className="mx-auto mt-2 flex max-w-[720px] flex-wrap items-center justify-center gap-7" style={{ marginTop: 30, fontSize: 13, color: L.inkSoft }}>
          {["Cancelás cuando quieras", "Soporte en español"].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <i className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full" style={{ background: L.violetSoft, color: L.violet, fontSize: 10, fontStyle: "normal" }}>✓</i>
              {t}
            </span>
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
.tl-feat-in { transition: opacity .4s, transform .4s cubic-bezier(.22,1,.36,1); }
@keyframes tl-badge-shine { to { left: 130%; } }
.tl-badge-pulse { position: relative; }
.tl-badge-pulse::after { content: ""; position: absolute; top: 0; bottom: 0; left: -70%; width: 45%; background: linear-gradient(105deg, transparent, rgba(255,255,255,.7), transparent); transform: skewX(-18deg); animation: tl-badge-sh 3.4s ease infinite; }
@keyframes tl-badge-sh { 0% { left: -70%; } 100% { left: 130%; } }
@media (prefers-reduced-motion: reduce) {
  .tl-feat-in { opacity: 1 !important; transform: none !important; }
  .tl-badge-pulse::after { animation: none !important; }
}
`,
        }}
      />
    </section>
  );
}
